/**
 * Contributor workspace routes: sign-in, onboarding and everything behind
 * the session wall. Moved verbatim out of `App.tsx` in Refactor v2 Phase 7;
 * paths are the public contract and stay unchanged.
 *
 * Routing rules, straight from the workspace handoff:
 *
 *   * `/signin` renders the OAuth surface; an authenticated visitor opening
 *     it is redirected to `/dashboard`. The server's own failure redirects
 *     land on `/sign-in?error=…` and `/sign-in/pending`, so those spellings
 *     are routes here too — same page, same states.
 *   * Protected routes require a session. An anonymous visitor is sent to
 *     `/signin?next=<where they were>`, and `next` survives the provider
 *     round trip via sessionStorage because the callback lands on a fixed
 *     path by design (no open redirect exists to reason about).
 *   * A newly admitted person completes onboarding once; Help reopens it.
 *   * Anything unmatched inside the shell is the shared "not available"
 *     page — the same experience a forbidden or missing task produces.
 */

import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AnalysisPage } from './pages/AnalysisPage.js';
import { AnalysisRunPage } from './pages/AnalysisRunPage.js';
import { BoardPage } from './pages/BoardPage.js';
import { LoadingPanel } from './pages/common.js';
import { ContributionsPage } from './pages/ContributionsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { DataRoomDeletedPage } from './pages/DataRoomDeletedPage.js';
import { DataRoomDocumentPage } from './pages/DataRoomDocumentPage.js';
import { DataRoomFolderPage } from './pages/DataRoomFolderPage.js';
import { DataRoomNewPage } from './pages/DataRoomNewPage.js';
import { DataRoomPage } from './pages/DataRoomPage.js';
import { NotAvailablePage } from './pages/NotAvailablePage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { NEXT_STORAGE_KEY, SignInPage, safeNextPath } from './pages/SignInPage.js';
import { TaskDetailPage } from './pages/TaskDetailPage.js';
import { TaskNewPage } from './pages/TaskNewPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { VideoPoolReviewPage } from './pages/VideoPoolReviewPage.js';
import { useSession } from './session.js';
import { AppShell } from './shell.js';
import { workspaceSlots } from './slots.js';

/**
 * The session wall around every workspace route.
 *
 * Loading renders a spinner — never a guess. Anonymous redirects to sign-in
 * with the current location preserved. A first-time contributor is routed
 * through onboarding before anything else, and `/onboarding` itself stays
 * reachable afterwards so Help can reopen it.
 */
export function RequireSession({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const { session } = useSession();
  const location = useLocation();

  if (session.status === 'loading') return <LoadingPanel />;

  if (session.status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  const onboarding = location.pathname === '/onboarding';
  if (!session.profile.onboardingCompleted && !onboarding) {
    return <RememberDestinationAndOnboard />;
  }

  return children;
}

/**
 * Route a first-time contributor to onboarding *without* losing where they
 * were going. The destination survives in the same storage slot the sign-in
 * flow uses, and the onboarding finish consumes it — so a deep link followed
 * by a first sign-in ends on the deep link, not on the dashboard.
 */
function RememberDestinationAndOnboard(): ReactNode {
  const location = useLocation();

  useEffect(() => {
    const destination = safeNextPath(location.pathname + location.search);
    if (destination !== null && destination !== '/onboarding') {
      try {
        sessionStorage.setItem(NEXT_STORAGE_KEY, destination);
      } catch {
        // Without storage the fallback is the dashboard, which is the same
        // behaviour the feature replaces — degraded, not broken.
      }
    }
  }, [location]);

  return <Navigate to="/onboarding" replace />;
}

/** Owner navigation is convenient; this wall also prevents a forbidden page flash. */
export function RequireOwner({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const { session } = useSession();
  if (session.status !== 'authenticated' || session.account.role !== 'OWNER') {
    return <NotAvailablePage />;
  }
  return children;
}

export function WorkspaceRoutes(): ReactNode {
  const { session } = useSession();
  if (session.status === 'authenticated' && session.account.role === 'MERCHANT') {
    return <NotAvailablePage />;
  }
  return (
    <RequireSession>
      <AppShell>
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/tasks" element={<BoardPage />} />
          <Route path="/tasks/new" element={<TaskNewPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/contributions" element={<ContributionsPage />} />
          <Route path="/data-room" element={<DataRoomPage />} />
          <Route path="/data-room/new" element={<DataRoomNewPage />} />
          {/* Before /data-room/:id, so "deleted" is a page, not a bad id. */}
          <Route path="/data-room/deleted" element={<DataRoomDeletedPage />} />
          <Route
            path="/data-room/category/:category"
            element={<DataRoomFolderPage />}
          />
          <Route path="/data-room/folder/:id" element={<DataRoomFolderPage />} />
          {/* Panels this build supplies, before the catch-all. */}
          {workspaceSlots().routes?.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route path="/data-room/:id" element={<DataRoomDocumentPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route
            path="/analysis"
            element={
              <RequireOwner>
                <AnalysisPage />
              </RequireOwner>
            }
          />
          <Route
            path="/analysis/:id"
            element={
              <RequireOwner>
                <AnalysisRunPage />
              </RequireOwner>
            }
          />
          <Route
            path="/video-pool/review"
            element={
              <RequireOwner>
                <VideoPoolReviewPage />
              </RequireOwner>
            }
          />
          <Route path="*" element={<NotAvailablePage />} />
        </Routes>
      </AppShell>
    </RequireSession>
  );
}

/** The canonical sign-in spelling, and the server's redirect targets. */
export function contributorSignInRoutes() {
  return (
    <>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-in/pending" element={<SignInPage variant="pending" />} />
    </>
  );
}

/**
 * Onboarding is authenticated but outside the shell: it is the one
 * full-screen moment the workspace has, and Help links back to it. The
 * catch-all hands everything else to the contributor shell.
 */
export function contributorWorkspaceRoutes() {
  return (
    <>
      <Route
        path="/onboarding"
        element={
          <RequireSession>
            <OnboardingPage />
          </RequireSession>
        }
      />
      <Route path="/*" element={<WorkspaceRoutes />} />
    </>
  );
}
